import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Download, ExternalLink } from "lucide-react";
import { getBook } from "@/lib/api";
import BookCard from "@/components/BookCard";
import Seo from "@/components/Seo";
import EbookShop from "@/components/EbookShop";

export default function BookDetail() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setData(null);
    setNotFound(false);
    getBook(slug).then(setData).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound)
    return (
      <div className="max-w-3xl mx-auto px-6 py-32 text-center">
        <h1 className="font-serif text-3xl text-[#0f2544]">Libro no encontrado</h1>
        <Link to="/libros" className="hv-link-underline text-[#1f44e0] mt-4 inline-block">← Volver al catálogo</Link>
      </div>
    );

  if (!data) return <div className="max-w-6xl mx-auto px-6 py-32 text-[#8a8a83]">Cargando...</div>;

  const { book, author, related } = data;
  const impreso = (book.purchase_links || []).filter((l) => l.tipo !== "ebook");
  const ebook = (book.purchase_links || []).filter((l) => l.tipo === "ebook");

  return (
    <>
      <Seo
        title={book.seo_title || book.titulo}
        description={book.seo_description || book.sinopsis_corta || book.sinopsis_completa?.slice(0, 155)}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Book",
          name: book.titulo,
          author: author ? { "@type": "Person", name: author.nombre } : undefined,
          inLanguage: book.idioma,
          isbn: book.isbn || undefined,
          publisher: { "@type": "Organization", name: "Editorial Harvest" },
          image: book.portada || undefined,
        }}
      />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-12 pt-10">
        <Link to="/libros" className="inline-flex items-center gap-2 text-sm text-[#8a8a83] hover:text-[#1f44e0] transition-colors" data-testid="back-to-books">
          <ArrowLeft size={16} /> Catálogo
        </Link>
      </div>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-12 py-10 md:py-14 grid md:grid-cols-[minmax(0,340px)_1fr] gap-10 lg:gap-16">
        <div className="mx-auto w-full max-w-[300px] md:sticky md:top-28 self-start">
          <div className="hv-book">
            {book.portada ? <img src={book.portada} alt={`Portada de ${book.titulo}`} /> : null}
          </div>
        </div>

        <div>
          {book.categoria_nombre && <p className="hv-overline">{book.categoria_nombre}</p>}
          <h1 className="font-serif text-3xl md:text-5xl leading-tight text-[#0f2544] mt-3" data-testid="book-title">
            {book.titulo}
          </h1>
          {book.subtitulo && <p className="font-serif text-xl md:text-2xl text-[#4a4a45] mt-2 italic">{book.subtitulo}</p>}
          {author && (
            <Link to={`/autores/${author.slug}`} className="hv-link-underline inline-block mt-4 text-[#1f44e0] font-medium" data-testid="book-author-link">
              {author.nombre}
            </Link>
          )}

          {(book.sinopsis_completa || book.sinopsis_corta) ? (
            <div className="mt-7 text-[#4a4a45] leading-relaxed whitespace-pre-line">
              {book.sinopsis_completa || book.sinopsis_corta}
            </div>
          ) : (
            <p className="mt-7 text-[#8a8a83] italic">Sinopsis disponible próximamente.</p>
          )}

          <dl className="mt-8 grid grid-cols-2 gap-y-3 gap-x-6 text-sm max-w-md border-t border-[#0f2544]/10 pt-6">
            {book.isbn && (<><dt className="text-[#8a8a83]">ISBN</dt><dd className="text-[#0f2544]">{book.isbn}</dd></>)}
            <dt className="text-[#8a8a83]">Idioma</dt><dd className="text-[#0f2544]">{book.idioma}</dd>
            {book.formato && (<><dt className="text-[#8a8a83]">Formato</dt><dd className="text-[#0f2544]">{book.formato}</dd></>)}
            {book.fecha_publicacion && (<><dt className="text-[#8a8a83]">Publicación</dt><dd className="text-[#0f2544]">{book.fecha_publicacion}</dd></>)}
          </dl>

          {/* Buy actions */}
          <div className="mt-9">
            {book.descarga_gratuita && book.archivo_descarga ? (
              <a href={book.archivo_descarga} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-[#1f44e0] px-8 py-4 text-xs font-semibold tracking-[0.12em] text-white hover:bg-[#0f2544] transition-colors" data-testid="book-download-free">
                <Download size={16} /> DESCARGAR GRATIS
              </a>
            ) : impreso.length > 0 ? (
              <div data-testid="book-buy">
                <p className="hv-overline mb-3">Comprar libro</p>
                <div className="flex flex-wrap gap-3">
                  {impreso.map((l) => (
                    <a key={l.id} href={l.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-[#0f2544] px-6 py-3 text-xs font-semibold tracking-[0.1em] text-white hover:bg-[#1f44e0] transition-colors" data-testid={`buy-${l.pais_o_region}`}>
                      {l.pais_o_region} <ExternalLink size={13} />
                    </a>
                  ))}
                </div>
              </div>
            ) : !book.venta && (
              <p className="text-sm text-[#8a8a83] italic">Puntos de venta disponibles próximamente.</p>
            )}

            {ebook.length > 0 && (
              <div className="mt-6">
                <p className="hv-overline mb-3">Comprar ebook</p>
                <div className="flex flex-wrap gap-3">
                  {ebook.map((l) => (
                    <a key={l.id} href={l.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full border border-[#0f2544]/25 px-6 py-3 text-xs font-semibold tracking-[0.1em] text-[#0f2544] hover:bg-[#0f2544] hover:text-white transition-colors">
                      {l.pais_o_region} <ExternalLink size={13} />
                    </a>
                  ))}
                </div>
              </div>
            )}
            {book.venta && <EbookShop book={book} />}
          </div>
        </div>
      </section>

      {/* Sobre el autor */}
      {author && (
        <section className="bg-[#eceae4]" data-testid="book-about-author">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-12 py-14 flex flex-col sm:flex-row gap-8 items-center sm:items-start">
            <div className="w-32 h-32 rounded-full overflow-hidden bg-[#e7e5df] shrink-0">
              {author.foto ? <img src={author.foto} alt={author.nombre} className="w-full h-full object-cover" /> : null}
            </div>
            <div className="text-center sm:text-left">
              <p className="hv-overline">Sobre el autor</p>
              <h3 className="font-serif text-2xl text-[#0f2544] mt-2">{author.nombre}</h3>
              {author.biografia_corta && <p className="text-[#4a4a45] mt-3 max-w-2xl leading-relaxed">{author.biografia_corta}</p>}
              <Link to={`/autores/${author.slug}`} className="hv-link-underline inline-block mt-4 text-sm font-semibold text-[#1f44e0]" data-testid="view-author-profile">
                VER PERFIL DEL AUTOR →
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Relacionados */}
      {related?.length > 0 && (
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-12 py-16">
          <h2 className="font-serif text-2xl md:text-3xl text-[#0f2544] mb-10">También puede interesarte</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-6 md:gap-8">
            {related.map((b) => (
              <BookCard key={b.slug} book={b} />
            ))}
          </div>
        </section>
      )}
    </>
  );
}
